/*
 * Arbortech product overview video — click-to-play mini showreel.
 *
 * Ported from arbortech-astro initMiniShowreelPlayer(). The Astro version used
 * GSAP Flip for the expand animation; the visual reveal (lightbox dark overlay,
 * card margin tween, embed fade-in, cover-image fade-out, play-button hide) is
 * already driven entirely by the [data-mini-showreel-status="active"] CSS in
 * arb-product-overview-video.css, so this self-contained custom element only
 * needs to toggle that status, play/stop the iframe embed, and manage scroll /
 * Escape / overlay-close. GSAP Flip is used as a progressive enhancement when
 * present, falling back to the pure-CSS transition otherwise.
 */
// Block-scoped so the file can run twice: arb-video-player loads it once per
// section, and a page with two players used to throw "Identifier
// 'ArbProductOverviewVideo' has already been declared" on the second copy.
{
class ArbProductOverviewVideo extends HTMLElement {
  connectedCallback() {
    this.name =
      this.querySelector('[data-mini-showreel-player]')?.getAttribute(
        'data-mini-showreel-player',
      ) || '';
    this.lightbox = this.querySelector('[data-mini-showreel-lightbox]');
    this.player = this.querySelector('[data-mini-showreel-player]');
    this.isOpen = false;

    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onVerticalChange = this.onVerticalChange.bind(this);

    /* Capture the route/query entry selection once. Pack changes are temporary;
       unticking the final vertical add-on must return here, including a valid
       ?vertical= override that differs from the server-resolved route. */
    this.entryVertical = this.getRequestedVertical();
    this.applyContext(this.entryVertical);
    this.addEventListener('click', this.onClick);
    document.addEventListener('keydown', this.onKeydown);
    /* arbt: ARB-189 — the PDP script (arb-main-product.liquid) dispatches this
       when a vertical-carrying add-on (MG1000 pack) is ticked or unticked. */
    document.addEventListener('arbt:vertical-change', this.onVerticalChange);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('arbt:vertical-change', this.onVerticalChange);
    window.removeEventListener('scroll', this.onScroll);
  }

  onVerticalChange(event) {
    const vertical = event && event.detail ? event.detail.vertical : null;
    const hasCheckedVerticalAddon = Boolean(
      document.querySelector('input[data-arbt-addon-vertical]:checked'),
    );
    this.applyContext(
      hasCheckedVerticalAddon && typeof vertical === 'string'
        ? vertical
        : this.entryVertical ?? this.getRequestedVertical(),
    );
  }

  onClick(event) {
    const openBtn = event.target.closest('[data-mini-showreel-open]');
    if (openBtn) {
      event.preventDefault();
      this.open(openBtn);
      return;
    }
    const closeBtn = event.target.closest('[data-mini-showreel-close]');
    if (closeBtn) {
      event.preventDefault();
      this.close();
    }
  }

  onKeydown(event) {
    if (event.key === 'Escape' && this.isOpen) this.close();
  }

  onScroll() {
    if (!this.isOpen) return;
    if (Math.abs(window.scrollY - this.openScrollY) > 80) this.close();
  }

  setStatus(status) {
    document
      .querySelectorAll(
        `[data-mini-showreel-lightbox="${this.name}"], [data-mini-showreel-player="${this.name}"]`,
      )
      .forEach((el) => el.setAttribute('data-mini-showreel-status', status));
  }

  getEmbed() {
    return this.player?.querySelector('[data-mini-showreel-embed]');
  }

  /* WCAG 2.4.3 (FAIL-A1): the reveal swaps .arb-video-band__play out of view and
     .arb-mini-showreel__close from display:none to display:grid, so the focus has
     to move with it — the same two-line pattern arb-projects-grid.js already uses
     for the project video modal (openModal L178 / closeModal L156). Both queries
     are scoped to `button[...]` on purpose: the snippet also carries a
     div[data-mini-showreel-open] click-catcher and a div[data-mini-showreel-close]
     backdrop, neither of which is focusable. */
  getCloseButton() {
    return this.querySelector('button[data-mini-showreel-close]');
  }

  getOpenButton() {
    return this.querySelector('button[data-mini-showreel-open]');
  }

  getCoverImage() {
    return (
      this.player?.querySelector('.mini-showreel__cover-image') ||
      this.querySelector('.arb-video-band__cover')
    );
  }

  /* PDP overview banner (sections/arb-product-overview-video.liquid) launches
     the shared arb-project-video-modal instead of the mini-showreel reveal;
     there the context data must land on the modal trigger's attributes. Page
     video sections (arb-video-player) have no triggers, so this is a no-op. */
  getModalTriggers() {
    return [...this.querySelectorAll('[data-arb-project-video-trigger]')];
  }

  /* The vertical is resolved SERVER-side by snippets/arb-context-vertical and
     arrives as data-active-vertical, so the collection-handle map lives in one
     place. Only ?vertical= is handled here — Liquid cannot read query strings,
     which is the sole reason this runs client-side at all. */
  getRequestedVertical() {
    const VERTICALS = ['power-carving', 'construction-restoration', 'outdoor-grounds-care'];
    const requested = new URLSearchParams(window.location.search).get('vertical');
    if (VERTICALS.includes(requested)) return requested;
    const active = this.getAttribute('data-active-vertical') || '';
    return VERTICALS.includes(active) ? active : '';
  }

  getContextValue(vertical, key) {
    if (!vertical) return '';
    return this.getAttribute(`data-context-${vertical}-${key}`) || '';
  }

  getContextBundle(vertical) {
    return {
      videoSrc: this.getContextValue(vertical, 'video-src'),
      title: this.getContextValue(vertical, 'title'),
      posterSrc: this.getContextValue(vertical, 'poster-src'),
      posterAlt: this.getContextValue(vertical, 'poster-alt'),
    };
  }

  getProductBundle() {
    return {
      videoSrc: this.getAttribute('data-default-video-src') || '',
      title: this.getAttribute('data-default-title') || '',
      posterSrc: this.getAttribute('data-default-poster-src') || '',
      posterAlt: this.getAttribute('data-default-poster-alt') || '',
    };
  }

  getOtherVerticalBundle(activeVertical) {
    const verticals = ['power-carving', 'construction-restoration', 'outdoor-grounds-care'];
    for (const vertical of verticals) {
      if (vertical === activeVertical) continue;
      const bundle = this.getContextBundle(vertical);
      if (bundle.videoSrc) return bundle;
    }
    return { videoSrc: '', title: '', posterSrc: '', posterAlt: '' };
  }

  applyContextFromUrl() {
    this.applyContext(this.getRequestedVertical());
  }

  applyContext(vertical) {
    const iframe = this.getEmbed();
    const image = this.getCoverImage();

    /* Captured on the first call (from connectedCallback, while the DOM is
       still the server render) so a later revert — unticking a pack after an
       arbt:vertical-change — can restore the entry state, srcset included. */
    if (!this.initialState) {
      const firstTrigger = this.getModalTriggers()[0];
      this.initialState = {
        embedSrc:
          (iframe instanceof HTMLIFrameElement ? iframe.getAttribute('data-embed-src') : '') ||
          (firstTrigger ? firstTrigger.getAttribute('data-video-url') : '') ||
          '',
        embedTitle:
          (iframe instanceof HTMLIFrameElement ? iframe.title : '') ||
          (firstTrigger ? firstTrigger.getAttribute('data-video-title') : '') ||
          '',
        posterSrc: image instanceof HTMLImageElement ? image.getAttribute('src') || '' : '',
        posterSrcset: image instanceof HTMLImageElement ? image.getAttribute('srcset') || '' : '',
        posterAlt: image instanceof HTMLImageElement ? image.alt || '' : '',
      };
    }

    /*
     * arbt-video-precedence: active-vertical > product > other-vertical
     *
     * ARB-273/275 made each product+vertical context independently owned and
     * migrated the locale/market video bundle before this order changed. The
     * legacy product bundle remains the rollback fallback. Select by URL and
     * carry title/poster/alt from that same source; never compose four sources.
     */
    const activeBundle = this.getContextBundle(vertical);
    const productBundle = this.getProductBundle();
    const otherVerticalBundle = this.getOtherVerticalBundle(vertical);
    const selectedBundle = activeBundle.videoSrc
      ? activeBundle
      : productBundle.videoSrc
        ? productBundle
        : otherVerticalBundle;

    const nextSrc = selectedBundle.videoSrc || this.initialState.embedSrc;
    const nextTitle =
      selectedBundle.title
      || this.getAttribute('data-fallback-title')
      || this.initialState.embedTitle;
    const posterSrc =
      selectedBundle.posterSrc
      || this.getAttribute('data-fallback-poster-src')
      || this.initialState.posterSrc;
    const posterAlt =
      selectedBundle.posterAlt
      || this.getAttribute('data-fallback-poster-alt')
      || this.initialState.posterAlt;

    if (iframe instanceof HTMLIFrameElement) {
      if (nextSrc) iframe.setAttribute('data-embed-src', nextSrc);
      if (nextTitle) iframe.title = nextTitle;
    }

    this.getModalTriggers().forEach((trigger) => {
      if (nextSrc) trigger.setAttribute('data-video-url', nextSrc);
      if (nextTitle) trigger.setAttribute('data-video-title', nextTitle);
    });

    if (image instanceof HTMLImageElement) {
      if (posterSrc) {
        if (posterSrc === this.initialState.posterSrc && this.initialState.posterSrcset) {
          image.setAttribute('srcset', this.initialState.posterSrcset);
        } else {
          image.removeAttribute('srcset');
        }
        image.src = posterSrc;
        if (posterAlt) image.alt = posterAlt;
      }
    }
  }

  normalizeEmbedSrc(src) {
    if (!src) return src;

    try {
      const url = new URL(src, window.location.href);
      const hostname = url.hostname.replace(/^www\./, '');

      if (hostname === 'youtu.be') {
        return `https://www.youtube.com/embed/${url.pathname.replace('/', '')}`;
      }

      if (hostname === 'youtube.com' || hostname === 'm.youtube.com') {
        const videoId = url.searchParams.get('v');
        if (videoId) return `https://www.youtube.com/embed/${videoId}`;

        const shortsMatch = url.pathname.match(/^\/shorts\/([^/]+)/);
        if (shortsMatch) return `https://www.youtube.com/embed/${shortsMatch[1]}`;
      }

      if (hostname === 'vimeo.com') {
        const videoId = url.pathname.split('/').filter(Boolean).pop();
        if (videoId) return `https://player.vimeo.com/video/${videoId}`;
      }
    } catch (error) {
      return src;
    }

    return src;
  }

  playEmbed() {
    const iframe = this.getEmbed();
    if (!(iframe instanceof HTMLIFrameElement)) return;
    const src = this.normalizeEmbedSrc(iframe.getAttribute('data-embed-src'));
    if (!src) return;
    iframe.src = src.includes('autoplay=1')
      ? src
      : `${src}${src.includes('?') ? '&' : '?'}autoplay=1`;
  }

  stopEmbed() {
    const iframe = this.getEmbed();
    if (iframe instanceof HTMLIFrameElement) iframe.src = '';
  }

  centerInViewport() {
    const section = this.closest('.arb-product-overview-video') || this;
    const rect = section.getBoundingClientRect();
    const maxScrollY = Math.max(
      0,
      document.documentElement.scrollHeight - window.innerHeight,
    );
    const targetY = Math.min(
      maxScrollY,
      Math.max(0, window.scrollY + rect.top - (window.innerHeight - rect.height) / 2),
    );

    window.scrollTo({
      top: targetY,
      left: window.scrollX,
      behavior: 'auto',
    });
  }

  open(trigger) {
    if (this.isOpen || !this.lightbox || !this.player) return;
    this.centerInViewport();
    this.isOpen = true;
    this.openScrollY = window.scrollY;
    /* Remember the real tab stop, not whatever was clicked: the click-catcher div
       is aria-hidden and unfocusable, so a pointer open still has to restore to
       the play button. */
    this.lastTrigger =
      trigger instanceof HTMLElement && trigger.tagName === 'BUTTON'
        ? trigger
        : this.getOpenButton();
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.setStatus('active');
    this.playEmbed();

    /* setStatus() has already flipped the attribute the display rule keys on, and
       focus() forces the style flush, so the close button is displayed by the time
       it is asked to take focus. */
    const closeButton = this.getCloseButton();
    if (closeButton instanceof HTMLElement) closeButton.focus();
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    window.removeEventListener('scroll', this.onScroll);
    this.stopEmbed();
    this.setStatus('not-active');

    const opener = this.lastTrigger instanceof HTMLElement ? this.lastTrigger : this.getOpenButton();
    if (opener instanceof HTMLElement) opener.focus();
  }
}

if (!customElements.get('arb-product-overview-video')) {
  customElements.define('arb-product-overview-video', ArbProductOverviewVideo);
}
}
